import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, RefreshCw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, Spinner } from "../components/common";

const NAMES = { USD: "US Dollar", HTG: "Haitian Gourde", EUR: "Euro", CAD: "Canadian Dollar", DOP: "Dominican Peso", GBP: "British Pound" };
const FLAG = { USD: "🇺🇸", HTG: "🇭🇹", EUR: "🇪🇺", CAD: "🇨🇦", DOP: "🇩🇴", GBP: "🇬🇧" };

export default function Converter() {
  const [supported, setSupported] = useState([]);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("HTG");
  const [amount, setAmount] = useState("100");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/fx/rates"); setSupported(r.data.supported); }
      catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const convert = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { setResult(null); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/fx/convert", { from_currency: from, to_currency: to, amount: val });
      if (data.error) { toast.error(data.error); setResult(null); }
      else setResult(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    const id = setTimeout(convert, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, amount, loading]);

  const swap = () => { setFrom(to); setTo(from); };

  if (loading) return <FullLoader />;

  const Select = ({ value, onChange, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-12 px-3 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm font-semibold appearance-none">
      {supported.map((c) => <option key={c} value={c}>{FLAG[c]} {c}</option>)}
    </select>
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Currency Converter" subtitle="Live USD, Gourde & more" />

      <div className="rounded-3xl bg-card border border-border p-5 space-y-4">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Amount</label>
          <div className="flex items-center gap-2 mt-1.5">
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="fx-amount-input" min="0" step="0.01"
              className="flex-1 min-w-0 h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-lg font-mono" />
            <Select value={from} onChange={setFrom} testid="fx-from-select" />
          </div>
        </div>

        <div className="flex justify-center">
          <button onClick={swap} data-testid="fx-swap-btn"
            className="grid place-items-center w-11 h-11 rounded-full bg-primary/10 text-primary transition-transform active:scale-90 hover:rotate-180 duration-300">
            <ArrowRightLeft size={18} />
          </button>
        </div>

        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Converted to</label>
          <div className="flex items-center gap-2 mt-1.5">
            <div data-testid="fx-result" className="flex-1 min-w-0 h-12 px-4 rounded-xl bg-secondary flex items-center text-lg font-mono overflow-hidden">
              {busy ? <Spinner className="w-4 h-4 text-primary" /> : (result ? <span className="truncate block w-full">{result.result.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> : "—")}
            </div>
            <Select value={to} onChange={setTo} testid="fx-to-select" />
          </div>
        </div>
      </div>

      {result && (
        <div className="mt-5 rounded-2xl bg-secondary/60 p-4 text-center">
          <p className="font-semibold" data-testid="fx-rate-text">{result.rate_text}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {NAMES[from]} → {NAMES[to]} · {result.source === "live" ? "Live rate" : "Approximate rate"}
          </p>
        </div>
      )}
    </div>
  );
}
